import {
  Checkbox,
  NumberInput,
  PasswordInput,
  Select,
  Stack,
  TagsInput,
  Textarea,
  TextInput,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import type { AdapterConfigField } from './types';

export interface AdapterConfigFieldsProps {
  fields: AdapterConfigField[];
  /**
   * Path prefix for `form.getInputProps`. Use `'config'` for the public
   * config object and `'secrets'` for encrypted secrets.
   */
  pathPrefix: 'config' | 'secrets';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturnType<any>;
  /** Disable all inputs (e.g. while submitting). */
  disabled?: boolean;
}

/**
 * Renders a list of inputs driven by [`AdapterConfigField`] entries.
 *
 * Field-type → control mapping:
 * - `text` / `url`        → `TextInput`
 * - `password`            → `PasswordInput`
 * - `select`              → `Select`
 * - `checkbox`            → `Checkbox`
 * - `number`              → `NumberInput`
 * - `email-list`          → `TagsInput` (one address per pill)
 * - `array-string`        → `TagsInput`
 * - `json`                → `Textarea` (raw JSON, parsed on submit by parent)
 *
 * The component never calls the API directly — submission happens in the
 * parent via `form.onSubmit`.
 */
export function AdapterConfigFields({
  fields,
  pathPrefix,
  form,
  disabled,
}: AdapterConfigFieldsProps) {
  const { t } = useTranslation();

  if (fields.length === 0) return null;

  return (
    <Stack gap="sm">
      {fields.map((field) => {
        const path = `${pathPrefix}.${field.key}`;
        const inputProps = form.getInputProps(path);
        const label = t(field.label);
        const description = field.helperText ? t(field.helperText) : undefined;
        const placeholder = field.placeholder;
        const baseProps = {
          label,
          description,
          required: field.required,
          disabled,
          placeholder,
        };

        switch (field.type) {
          case 'password':
            return (
              <PasswordInput
                key={field.key}
                {...baseProps}
                {...inputProps}
                autoComplete="new-password"
              />
            );
          case 'select':
            return (
              <Select
                key={field.key}
                {...baseProps}
                data={(field.options ?? []).map((o) => ({
                  value: o.value,
                  label: t(o.label),
                }))}
                {...inputProps}
              />
            );
          case 'checkbox':
            return (
              <Checkbox
                key={field.key}
                label={label}
                description={description}
                disabled={disabled}
                {...inputProps}
                checked={Boolean(inputProps.value)}
              />
            );
          case 'number':
            return (
              <NumberInput key={field.key} {...baseProps} {...inputProps} allowDecimal={false} />
            );
          case 'email-list':
          case 'array-string':
            return (
              <TagsInput
                key={field.key}
                {...baseProps}
                clearable
                value={Array.isArray(inputProps.value) ? inputProps.value : []}
                onChange={(val) => form.setFieldValue(path, val)}
              />
            );
          case 'json':
            return (
              <Textarea
                key={field.key}
                {...baseProps}
                autosize
                minRows={3}
                maxRows={10}
                {...inputProps}
                value={
                  typeof inputProps.value === 'string'
                    ? inputProps.value
                    : inputProps.value !== undefined && inputProps.value !== null
                      ? JSON.stringify(inputProps.value, null, 2)
                      : ''
                }
              />
            );
          default:
            return <TextInput key={field.key} {...baseProps} {...inputProps} />;
        }
      })}
    </Stack>
  );
}
